import React, {useMemo} from "react";
import {useTranslation} from "react-i18next";
import {useQuery} from "@apollo/client";
import {query, QueryData, QueryVars} from "./project.graphql";
import {useParams} from "react-router";
import {reverse} from "../../routing";
import {isSharedId} from "../../types";
import {getMapByObjectProperty} from "../../utils";
import useSortedChallenges from "../../hooks/useSortedChallenges";

import ReactDocumentTitle from "../../components/ReactDocumentTitle";
import Layout, {Header, Main} from "../../components/Layout";
import Container from "../../ui/container";
import ChallengeCard from "../../components/ChallengeCard";
import Breadcrumbs from "../../ui/breadcrumbs";
import ReactLink from "../../components/ReactLink";

import classes from "./project.module.css";

export default function PlayerProjectScene() {
  const {t} = useTranslation();

  const {id: projectId} = useParams<{ id: string }>();
  const isShared = isSharedId(projectId);

  const {data, loading: fetching} = useQuery<QueryData, QueryVars>(query, {
    fetchPolicy: "network-only",
    variables: {
      projectId: projectId
    }
  });

  const {project, challengesStates} = data ?? {};
  const loading = !project && fetching;

  const mappedChallengesStates = useMemo(() => {
    if (!challengesStates) {
      return undefined;
    }
    return getMapByObjectProperty(challengesStates, "challengeId");
  }, [challengesStates]);

  const [activeChallenges, completedChallenges] = useSortedChallenges(
    project?.challenges ?? [],
    mappedChallengesStates,
  );

  return (
    <>
      <ReactDocumentTitle title={project?.name ?? t("playerIndex.title")}/>

      <Layout>
        <Header/>
        <Main>
          <Container className={classes.main}>
            <Container className={classes.challenges}>
              <Breadcrumbs className={classes.breadcrumbs}>
                {!isShared ? (
                  <ReactLink href={reverse("player")}>{t("breadcrumbs.player")}</ReactLink>
                ) : (
                  <span>{t("breadcrumbs.player")}</span>
                )}
                <span>{project?.name ?? t("playerIndex.title")}</span>
              </Breadcrumbs>
              <h1 className={classes.h1}>{project?.name ?? t("playerIndex.title")}</h1>

              {!loading && project && (
                <>
                  {activeChallenges.length === 0 && completedChallenges.length === 0 && (
                    <div className={classes.emptyState}>{t("playerIndex.emptyState")}</div>
                  )}

                  {activeChallenges.length > 0 && (
                    <>
                      {completedChallenges.length > 0 && (
                        <h3 className={classes.assignedCompletedHeader}>{t("playerIndex.assigned")}</h3>
                      )}
                      <div className={classes.challengeItems}>
                        {activeChallenges.map((item) => (
                          <ChallengeCard
                            key={item.id}
                            className={classes.challengeItem}
                            challenge={item}
                            project={project}
                            {...(isShared ? {
                              isShared: true
                            } : {
                              isShared: false,
                              challengeState: mappedChallengesStates!.get(item.id)!
                            })}
                          />
                        ))}
                      </div>
                    </>
                  )}

                  {completedChallenges.length > 0 && (
                    <>
                      <h3 className={classes.assignedCompletedHeader}>{t("playerIndex.completed")}</h3>
                      <div className={classes.challengeItems}>
                        {completedChallenges.map((item) => (
                          <ChallengeCard
                            key={item.id}
                            className={classes.challengeItem}
                            challenge={item}
                            project={project}
                            {...(isShared ? {
                              isShared: true
                            } : {
                              isShared: false,
                              challengeState: mappedChallengesStates!.get(item.id)!
                            })}
                          />
                        ))}
                      </div>
                    </>
                  )}
                </>
              )}

              {loading && (
                <div className={classes.challengeItems}>
                  <ChallengeCard.Skeleton className={classes.challengeItem}/>
                  <ChallengeCard.Skeleton className={classes.challengeItem}/>
                  <ChallengeCard.Skeleton className={classes.challengeItem}/>
                </div>
              )}
            </Container>
          </Container>
        </Main>
      </Layout>
    </>
  )
}
